import React from "react";
import {
  Play,
  Pause,
  Heart,
  X,
  Volume2,
  VolumeX,
  Loader2,
  MapPin,
  Music2,
  Share2,
  Moon,
  SkipBack,
  SkipForward,
  Megaphone,
  Cast,
  SignalLow,
} from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { tap, confirm as hapticConfirm } from "../lib/haptics";
import { absoluteUrl } from "../lib/share";
import { slugIfKnown, stationPath } from "../lib/stationUrls";
import ShareDialog from "./ShareDialog";
import AlarmButton from "./AlarmButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";

const Equalizer = () => (
  <div className="rm-eq flex h-4 items-end gap-[3px]">
    {[0, 0.15, 0.3, 0.45, 0.2].map((d, i) => (
      <span key={i} style={{ animationDelay: `${d}s` }} />
    ))}
  </div>
);

const StationLogo = ({ station }) => {
  const [err, setErr] = React.useState(false);
  const letter = (station.name || "?").trim().charAt(0).toUpperCase();
  if (station.favicon && !err) {
    return (
      <img
        src={station.favicon}
        alt=""
        onError={() => setErr(true)}
        className="h-full w-full rounded-xl object-cover"
      />
    );
  }
  return (
    <div className="flex h-full w-full items-center justify-center rounded-xl bg-gradient-to-br from-[#123a2b] to-[#0a1a14] font-display text-2xl font-700 text-[#2fe08a]">
      {letter}
    </div>
  );
};

const SLEEP_OPTIONS = [15, 30, 45, 60];

const SleepTimer = () => {
  const { sleepEndsAt, sleepRemaining, startSleepTimer, cancelSleepTimer } =
    usePlayer();
  const active = Boolean(sleepEndsAt);
  const mins = Math.floor(sleepRemaining / 60000);
  const secs = Math.floor((sleepRemaining % 60000) / 1000);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`flex h-10 items-center justify-center gap-1.5 rounded-full px-2.5 transition-colors ${
            active
              ? "bg-[#2fe08a]/15 text-[#7bf0b8]"
              : "text-[#9fb3aa] hover:bg-white/5 hover:text-white"
          }`}
          title="Sleep timer"
        >
          <Moon size={18} />
          {active && (
            <span className="text-xs font-500 tabular-nums">
              {mins}:{String(secs).padStart(2, "0")}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-52 rounded-xl border-[#2fe08a]/20 bg-[#0a1014] p-3 text-[#e8f0ec]"
      >
        <div className="mb-2 flex items-center gap-2 font-display text-sm font-600">
          <Moon size={15} className="text-[#2fe08a]" /> Sleep timer
        </div>
        <p className="mb-3 text-xs text-[#8497a0]">
          Playback gently fades out and stops.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {SLEEP_OPTIONS.map((m) => (
            <button
              key={m}
              onClick={() => startSleepTimer(m)}
              className="rounded-lg bg-white/5 py-2 text-sm font-500 text-white transition-colors hover:bg-[#2fe08a]/20 hover:text-[#7bf0b8]"
            >
              {m} min
            </button>
          ))}
        </div>
        {active && (
          <button
            onClick={cancelSleepTimer}
            className="mt-2 w-full rounded-lg border border-rose-400/30 py-2 text-sm text-rose-300 transition-colors hover:bg-rose-400/10"
          >
            Cancel timer
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

const FavoriteButton = ({ active, onClick, className = "" }) => (
  <button
    onClick={onClick}
    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all ${
      active ? "text-rose-400" : "text-[#9fb3aa] hover:bg-white/5 hover:text-white"
    } ${className}`}
    title="Favorite"
  >
    <Heart size={20} fill={active ? "currentColor" : "none"} />
  </button>
);

import { ListMusic } from "lucide-react";

const PlayerBar = () => {
  // Opens the desktop share dialog (QR code path). Declared up here because the
  // component returns early when nothing is playing.
  const [shareOpen, setShareOpen] = React.useState(false);
  const {
    current,
    isPlaying,
    isBuffering,
    switching,
    error,
    nowPlaying,
    toggle,
    next,
    prev,
    stop,
    volume,
    setVolume,
    isFavorite,
    toggleFavorite,
    adPlaying,
    weakSignal,
    flakyStation,
    castState,
    castAvailable,
    startCast,
    listLabel,
  } = usePlayer();

  if (!current) return null;
  const fav = isFavorite(current.id);

  // Sharing opens a sheet of its own: a QR code to move the station onto a phone,
  // the card image, and the raw link. On a phone the native share sheet is one of
  // the options there; on a desktop the QR is the whole point, because the person
  // you are sending it to is the one holding the phone.
  const share = () => setShareOpen(true);

  // The share link is the readable address, /station/<slug>/. The exact slug
  // comes from the build's index and is usually already resolved by the time
  // anyone presses share, because the address-bar effect fetches it the moment
  // the station changes; slugIfKnown falls back to the same slug the app would
  // have guessed if it somehow is not there yet.
  const shareLink = current
    ? absoluteUrl(stationPath(slugIfKnown(current.id, current.name)))
    : "";

  const place =
    [current.state, current.country].filter(Boolean).join(", ") || "On air";

  return (
    <div className="rm-safe-bottom pointer-events-none absolute inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-3 sm:px-6 sm:pb-5">
      {shareOpen && current && (
        <ShareDialog
          station={current}
          link={shareLink}
          onClose={() => setShareOpen(false)}
        />
      )}
      <div className="rm-fade-up pointer-events-auto w-full max-w-3xl rounded-2xl rm-glass px-3 py-3 shadow-[0_10px_40px_rgba(0,0,0,0.5)] sm:px-4">
        {/*
          On phones this is deliberately two rows: the station name gets the full
          width beside the logo, and the transport controls get a row of their
          own. Squeezing a logo, a title and five buttons into one 360px row left
          the name as a couple of truncated characters.
        */}
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex w-full min-w-0 items-center gap-3 sm:w-auto sm:flex-1">
            <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl">
              <StationLogo station={current} />
              {isPlaying && (
                <div className="absolute inset-0 flex items-end justify-center bg-black/35 pb-2">
                  <Equalizer />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="line-clamp-2 font-display text-[15px] font-600 leading-tight text-white sm:truncate">
                {adPlaying ? "Advertisement" : current.name || "Unknown station"}
              </div>
              {adPlaying ? (
                <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-[#ffcf8a]">
                  <Megaphone size={12} className="flex-shrink-0" />
                  <span className="truncate">
                    Back to {current.name || "your station"} after this break
                  </span>
                </div>
              ) : nowPlaying ? (
                <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-[#7bf0b8]">
                  <Music2 size={12} className="flex-shrink-0" />
                  <span className="truncate">{nowPlaying}</span>
                </div>
              ) : (
                <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-[#9fb3aa]">
                  <MapPin size={12} className="flex-shrink-0 text-[#2fe08a]" />
                  <span className="truncate">{place}</span>
                </div>
              )}
              {listLabel && (
                <div className="mt-1 flex min-w-0">
                  <span
                    className="flex min-w-0 items-center gap-1 rounded-full bg-[#2fe08a]/10 px-2 py-0.5 text-[10px] font-500 text-[#7bf0b8]"
                    title="Next and Back stay inside this list, not the whole catalogue"
                  >
                    <ListMusic size={10} className="flex-shrink-0" />
                    <span className="truncate">Next/Back: {listLabel}</span>
                  </span>
                </div>
              )}
              {error && (
                <div className="mt-0.5 truncate text-[11px] text-rose-400">
                  {error}
                </div>
              )}
              {/* Advisory only: the player already handles a bad stream by itself. This
                  is so a rescue never looks like a glitch. */}
              {!adPlaying && !error && (weakSignal || flakyStation) && (
                <div
                  className={`mt-0.5 flex items-center gap-1 truncate text-[11px] ${
                    weakSignal ? "text-amber-300" : "text-[#8fa79d]"
                  }`}
                  title={
                    weakSignal
                      ? "This stream has stalled more than once in the last two minutes"
                      : "This station has failed on this device before"
                  }
                >
                  <SignalLow size={12} className="flex-shrink-0" />
                  <span className="truncate">
                    {weakSignal
                      ? "Weak signal — Next will pick a stronger one"
                      : "Usually drops out — we'll switch if it does"}
                  </span>
                </div>
              )}
            </div>

            {/* Phone: heart sits up here so the control row stays roomy */}
            <FavoriteButton
              active={fav}
              onClick={() => toggleFavorite(current)}
              className="sm:hidden"
            />
          </div>

          <div className="flex w-full items-center justify-center gap-1.5 sm:w-auto sm:justify-end sm:gap-2">
            <div className="hidden items-center gap-2 md:flex">
              <button
                onClick={() => setVolume(volume > 0 ? 0 : 0.9)}
                className="text-[#9fb3aa] transition-colors hover:text-white"
              >
                {volume > 0 ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="h-1 w-20 cursor-pointer appearance-none rounded-full bg-white/15 accent-[#2fe08a]"
              />
            </div>

            <SleepTimer />

            <AlarmButton />

            {castState !== "unsupported" && (
              <button
                onClick={() => {
                  tap();
                  startCast();
                }}
                className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all hover:bg-white/5 ${
                  castState === "connected"
                    ? "bg-[#2fe08a]/15 text-[#7bf0b8]"
                    : "text-[#9fb3aa] hover:text-white"
                }`}
                title={
                  castState === "connected"
                    ? "Playing on another device — tap to change"
                    : castState === "airplay"
                      ? "Play on AirPlay (Control Centre)"
                      : castAvailable === false
                        ? "No cast device found on this Wi-Fi"
                        : "Play on a TV or speaker"
                }
              >
                <Cast size={18} />
              </button>
            )}

            <button
              onClick={share}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-[#9fb3aa] transition-all hover:bg-white/5 hover:text-white"
              title="Share this station"
            >
              <Share2 size={18} />
            </button>

            <FavoriteButton
              active={fav}
              onClick={() => {
                hapticConfirm();
                toggleFavorite(current);
              }}
              className="hidden sm:flex"
            />

            <button
              onClick={() => {
                tap();
                prev();
              }}
              className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full transition-all hover:bg-white/5 hover:text-white ${
                switching && switching.source === "prev" ? "text-[#2fe08a]" : "text-[#9fb3aa]"
              }`}
              title="Previous station"
            >
              {/* The old station keeps playing while the next one is prepared, so
                  the button has to show that the press landed. */}
              {switching && switching.source === "prev" ? (
                <Loader2 size={18} className="rm-spin" />
              ) : (
                <SkipBack size={19} fill="currentColor" />
              )}
            </button>

            
            <button
              onClick={() => {
                tap();
                next();
              }}
              className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full transition-all hover:bg-white/5 hover:text-white ${
                switching && switching.source === "next" ? "text-[#2fe08a]" : "text-[#9fb3aa]"
              }`}
              title="Next station"
            >
              {switching && switching.source === "next" ? (
                <Loader2 size={18} className="rm-spin" />
              ) : (
                <SkipForward size={19} fill="currentColor" />
              )}
            </button>

            <button
              onClick={stop}
              className="hidden h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[#6f857b] transition-colors hover:bg-white/5 hover:text-white sm:flex"
              title="Stop"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerBar;
